'use client';

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';

// import { PreferFilterForm } from '@/app/actions/filters/types';
import { ChildrenProps } from '@/types/common.types';

import { useSearchParamsResult } from '@/hooks/useSearchParamsResult';

import { NormalizePreferFilter } from '@/api/types/filters.types';

import { FiltersRowsEnum } from '@/enums/filters-rows.enums';

import { Option } from '@/components/Form/Select/context/SelectProvider';

// =================================================================

export type PreferFilterForm = {
  [FiltersRowsEnum.COUNTRIES]: Option[];
  [FiltersRowsEnum.GENRES]: Option[];
  [FiltersRowsEnum.QUALITIES]: Option[];
  [FiltersRowsEnum.CINEMAS]: Option[];
  [FiltersRowsEnum.YEARS]?: Option;
  [FiltersRowsEnum.RATING_IMDB]?: Option;
  [FiltersRowsEnum.RATING_KINOPOISK]?: Option;
  [FiltersRowsEnum.IS_FREE]: boolean;
  [FiltersRowsEnum.AUDIO_TRACK]: string;
  [FiltersRowsEnum.IS_DOWNLOAD]: boolean;
  [FiltersRowsEnum.IS_SUBTITLE_LOCALE]: boolean;
};

export type FormPreferFilter = {
  [key: string]: Option[] | Object | boolean | undefined;
};

export type FilterMovieContextType = NormalizePreferFilter & {
  onSubmitted?: () => void;
  initialValues?: FormPreferFilter;
  methods: UseFormReturn<PreferFilterForm>;
  handleChange: (props: {
    name: FiltersRowsEnum | FiltersRowsEnum;
    value: string | string[] | boolean;
  }) => void;
  onReset: () => void;
  toggleFiltersModal?: () => void;
  isLoading?: boolean;
  generateRatingOptions: (from: number, to: number) => Option[];
  yearOptions: Option[];
};

type YearOption = {
  from?: number;
  to?: number;
};

// =================================================================

export const findInitialValue = (
  options: Option[],
  value: string | string[] | null,
): Option | Option[] => {
  if (Array.isArray(value)) {
    return options.filter(option => value.includes(String(option.value)));
  }

  const foundOption = options.find(option => String(option.value) === value);
  return foundOption ? foundOption : [];
};

// =================================================================

export const yearList = [
  { from: 2025 },
  { from: 2024 },
  { from: 2023 },
  { from: 2022 },
  { from: 2021 },
  { from: 2015, to: 2020 },
  { from: 2010, to: 2015 },
  { from: 2005, to: 2010 },
  { from: 2000, to: 2005 },
  { from: 1995, to: 2000 },
  { from: 1990, to: 1995 },
  { from: 1980, to: 1990 },
];

const recipeMap: Map<string, YearOption> = new Map();

yearList.forEach(option => {
  const key = [option.from, option.to].filter(Boolean).join('-');
  recipeMap.set(key, option);
});

const yearOptions = yearList.map(year => {
  const item = [year.from, year.to].filter(Boolean).join('-');

  return {
    label: item,
    value: item,
  };
});

// =================================================================

const initialValues: PreferFilterForm = {
  genres: [],
  countries: [],
  years: undefined,
  qualities: [],
  cinemas: [],
  audioTrack: '',
  isFree: false,
  isDownload: false,
  isSubtitleLocales: false,
  ratingImdb: undefined,
  ratingKP: undefined,
};

// =================================================================

const FilterMovieContext = createContext<FilterMovieContextType | null>(null);

// =================================================================

export const FilterMovieProvider = (
  props: Omit<
    FilterMovieContextType,
    | 'methods'
    | 'onReset'
    | 'onSubmit'
    | 'isAnyFilterSelected'
    | 'handleChange'
    | 'generateRatingOptions'
    | 'yearOptions'
  > &
    ChildrenProps,
) => {
  const { children, countries, genres, qualities, cinemas, onSubmitted, isLoading, ...rest } =
    props;

  const t = useTranslations();
  const { queryParams, clearQueryParams, mergeQueryParams } = useSearchParamsResult();
  const searchParams = useSearchParams();

  const generateRatingOptions = (from: number, to: number): Option[] => {
    return Array.from({ length: to - from + 1 }, (_, i) => ({
      label: t('common.ratingPoint', {
        rating: `${from + i}`,
      }),
      value: `${from + i}`,
    })).reverse();
  };

  const initialValue: FormPreferFilter = useMemo(() => {
    return {
      countries: searchParams.getAll('countries')
        ? findInitialValue(countries, searchParams.getAll('countries'))
        : [],
      genres: searchParams.getAll('genres')
        ? findInitialValue(genres, searchParams.getAll('genres'))
        : [],
      qualities: searchParams.getAll('qualities')
        ? findInitialValue(qualities, searchParams.getAll('qualities'))
        : [],
      years: findInitialValue(
        yearOptions,
        [...searchParams.getAll('yearFrom'), ...searchParams.getAll('yearTo')]
          .filter(Boolean)
          .join('-'),
      ),
      isFree: queryParams.isFree === 'true',
      isDownload: queryParams.isDownload === 'true',
      audioTrack: queryParams.audioTrack === 'uz',
      ratingImdb: searchParams.getAll('ratingImdb')
        ? findInitialValue(generateRatingOptions(6, 9), searchParams.getAll('ratingImdb')[0])
        : undefined,
      ratingKP: searchParams.getAll('ratingKP')
        ? findInitialValue(generateRatingOptions(6, 9), searchParams.getAll('ratingKP')[0])
        : undefined,
      cinemas: searchParams.getAll('cinemas')
        ? findInitialValue(cinemas, searchParams.getAll('cinemas'))
        : [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, genres, qualities, yearOptions, cinemas, searchParams]);

  const methods = useForm<PreferFilterForm>({
    defaultValues: initialValue,
    mode: 'onChange',
  });

  const handleChange = ({
    name,
    value,
  }: {
    name: FiltersRowsEnum | FiltersRowsEnum;
    value: string | string[] | boolean;
  }) => {
    if (name === FiltersRowsEnum.YEARS) {
      const years = recipeMap.get(value as string);

      mergeQueryParams({
        [FiltersRowsEnum.YEAR_FROM]: years?.from,
        [FiltersRowsEnum.YEAR_TO]: years?.to,
      });

      return;
    } else {
      mergeQueryParams({
        [name]: value,
      });
    }
  };

  useEffect(() => {
    methods.reset(initialValue);
  }, [initialValue, methods]);

  const onReset = () => {
    methods.reset(initialValues);
    clearQueryParams();
  };

  return (
    <FilterMovieContext.Provider
      value={{
        ...rest,
        initialValues,
        countries,
        cinemas,
        genres,
        qualities,
        yearOptions,
        methods,
        handleChange,
        onReset,
        toggleFiltersModal: onSubmitted,
        isLoading,
        generateRatingOptions,
      }}
    >
      {children}
    </FilterMovieContext.Provider>
  );
};

// =================================================================

export const useFilterMovieContext = () => {
  const context = useContext(FilterMovieContext);

  if (!context) {
    throw new Error('useFilterMovieContext must be used within a FilterMovieContext');
  }

  return context;
};
